import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import type { OverrideRow, ReprocessMethod } from '../api/types';
import { Portal } from './Portal';

interface Props {
  storeId: string;
  row: OverrideRow;
  onClose: () => void;
  onResolved: () => void;
}

const POLL_MS = 10_000;

/** ui-light (2026-08-27): re-cuts run on the render job, never in-process.
 *  "Queue" writes the override row (the next run drains it); "Queue & run now"
 *  also triggers the job scoped to THIS product (--only-overrides --only-ids)
 *  and polls until the execution lands — a legible ~2–4 minute wait instead
 *  of a silent spinner (brief §8.3). */
export function ReprocessDialog({ storeId, row, onClose, onResolved }: Props) {
  const [method, setMethod] = useState<ReprocessMethod>('tier_1');
  const [phase, setPhase] = useState<'idle' | 'queueing' | 'running' | 'error'>('idle');
  const [runError, setRunError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const errText = (e: unknown) =>
    e instanceof HttpError ? e.detail : (e as Error).message;

  const enqueueM = useMutation({
    mutationFn: () => api.bulkEnqueueReprocess(storeId, [row.product_id], method),
    onSuccess: () => onResolved(),
  });

  async function queueAndRun() {
    setPhase('queueing');
    setRunError(null);
    try {
      await api.bulkEnqueueReprocess(storeId, [row.product_id], method);
      const run = await api.reprocessQueueRun(storeId, 'overrides', [row.product_id]);
      if (!alive.current) return;
      setPhase('running');
      const started = Date.now();
      // Poll until the execution completes. A single-product run typically
      // lands in 2–4 minutes; give up reporting after 20 (the job keeps
      // running server-side either way).
      for (;;) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        if (!alive.current) return;
        setElapsed(Math.round((Date.now() - started) / 1000));
        const { status } = await api.jobExecutionStatus(storeId, run.execution);
        if (status === 'succeeded') {
          onResolved();
          return;
        }
        if (status === 'failed') {
          setPhase('error');
          setRunError(
            'The render-job execution failed — check the job logs. The queued override stays pending for the next run.',
          );
          return;
        }
        if (Date.now() - started > 20 * 60_000) {
          setPhase('error');
          setRunError(
            'Still running after 20 minutes — the job continues server-side; close this and refresh later.',
          );
          return;
        }
      }
    } catch (e) {
      if (!alive.current) return;
      setPhase('error');
      setRunError(errText(e));
    }
  }

  const busy = phase === 'queueing' || phase === 'running';

  return (
    <Portal>
      <div className="fixed inset-0 bg-ff-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div
          className="bg-white rounded-2xl shadow-hover border border-ff-sand/70 p-6 max-w-xl w-full max-h-[90vh] overflow-auto"
          role="dialog"
          aria-modal="true"
          aria-label={`Re-cut ${row.product_id}`}
        >
          <div className="flex justify-between items-start mb-5">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute mb-1">
                Re-cut
              </p>
              <h2 className="font-display text-2xl text-ff-forest">
                <span className="font-mono text-base text-ff-ink/80">{row.product_id}</span>
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-ff-mute hover:text-ff-forest text-xl leading-none"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {busy ? (
            <div className="text-center py-10" data-testid="run-progress">
              <div className="animate-spin h-8 w-8 mx-auto border-4 border-ff-forest border-t-transparent rounded-full mb-3" />
              {phase === 'queueing' ? (
                <p className="text-ff-mute text-sm">Queueing + starting the render job…</p>
              ) : (
                <p className="text-ff-mute text-sm">
                  Re-cut job running for this product — typically 2–4 minutes. Elapsed{' '}
                  {elapsed}s.
                </p>
              )}
              <p className="text-[11px] text-ff-mute mt-2">
                The job runs server-side; closing this dialog does not cancel it.
              </p>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="flex flex-wrap gap-3 justify-center items-center">
                <select
                  data-testid="method-select"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as ReprocessMethod)}
                  className="ff-input w-auto px-3 py-2 text-sm"
                >
                  <option value="tier_0">Tier 0 — rembg lite</option>
                  <option value="tier_1">Tier 1 — BiRefNet full</option>
                  <option value="tier_2">Tier 2 — threshold packshot</option>
                  <option value="ben2">BEN2 — matting</option>
                  <option value="floodfill">Flood-fill</option>
                  <option value="passthrough">Passthrough (already cut)</option>
                  <option value="snapedit">Snapedit (external)</option>
                </select>
                <button className="ff-btn-ghost" onClick={() => enqueueM.mutate()}>
                  Queue
                </button>
                <button
                  className="ff-btn-primary"
                  data-testid="queue-and-run"
                  onClick={() => void queueAndRun()}
                >
                  Queue &amp; run job now
                </button>
              </div>
              <p className="text-[11px] text-ff-mute mt-4">
                “Queue” waits for the next scheduled run. “Queue &amp; run” renders just
                this product now (~2–4 min). For a stubborn image, upload a cutout PNG from
                the product’s QA card instead.
              </p>
              {(runError || enqueueM.isError) && (
                <p className="text-ff-danger text-sm mt-4" data-testid="run-error">
                  {runError ?? errText(enqueueM.error)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
